/**
 * Word Count Tab Component for ReportViewer
 * 
 * Displays word count optimization tools in the ReportViewer tab
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Modal } from '@tracekit/core';
import { getReport } from '@tracekit/core';
import { WordCountOptimizer } from './WordCountOptimizer';
import { WordCountDisplay, calculateReportWordCount } from './WordCountDisplay';
import { WordLimitConfiguration } from './WordLimitConfiguration';
import { validateWordCount, updateWordLimits } from '../services/rfpApi';
import { Settings, Scissors, AlertCircle } from 'lucide-react';

interface WordCountTabProps {
  reportId: string;
  report: any;
  enableOptimizer?: boolean; // Feature flag to show/hide optimizer button
}

export function WordCountTab({ reportId, report: initialReport, enableOptimizer = true }: WordCountTabProps) {
  const queryClient = useQueryClient();
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [showLimitConfig, setShowLimitConfig] = useState(false);
  
  // Validate reportId is a string UUID, not a number
  const validReportId = typeof reportId === 'string' && reportId.length > 10 ? reportId : null;
  if (!validReportId) {
    console.error('[WordCountTab] Invalid reportId provided:', reportId, typeof reportId);
    return (
      <Card>
        <div className="p-4 text-center text-red-600">
          Error: Invalid report ID. Cannot load word count data.
        </div>
      </Card>
    );
  }
  
  // Fetch report if not provided
  const { data: report = initialReport } = useQuery({
    queryKey: ['report', validReportId],
    queryFn: () => {
      // Double-check reportId is valid before making API call
      if (typeof validReportId !== 'string' || validReportId.length < 10) {
        throw new Error(`Invalid reportId: ${validReportId}`);
      }
      return getReport(validReportId);
    },
    enabled: !initialReport && !!validReportId,
    initialData: initialReport,
  });
  
  // Validate word count
  const { data: validation } = useQuery({
    queryKey: ['word-count-validation', validReportId],
    queryFn: () => validateWordCount(validReportId),
    enabled: !!validReportId && !!report,
  });
  
  if (!report) {
    return (
      <Card>
        <div className="p-4 text-center text-gray-500">Loading report...</div>
      </Card>
    );
  }
  
  const wordCount = calculateReportWordCount(report.sections || []);
  const wordLimit = (report.meta as any)?.word_limit;
  const sectionWordCounts = validation?.section_word_counts;
  // Map section limits from section names (from validation or report.meta) to section IDs (for form)
  const sectionLimitsById: Record<string, number> = {};
  const sectionLimitsFromMeta = (report.meta as any)?.section_limits || {};
  const sectionLimitsFromValidation = validation?.section_limits || {};
  // Use validation limits if available, otherwise fall back to meta
  const sectionLimitsByName = Object.keys(sectionLimitsFromValidation).length > 0 
    ? sectionLimitsFromValidation 
    : sectionLimitsFromMeta;
  
  if (sectionLimitsByName && report.sections) {
    report.sections.forEach((section: any) => {
      const sectionId = section.id || section.section_id;
      const sectionName = section.section_name || section.name;
      if (sectionId && sectionName && sectionLimitsByName[sectionName]) {
        sectionLimitsById[sectionId] = sectionLimitsByName[sectionName];
      }
    });
  }
  
  return (
    <>
      <div className="space-y-6">
        {/* Word Count Summary */}
        <Card title="Word Count Summary">
          <div className="space-y-4">
            <WordCountDisplay
              wordCount={wordCount}
              wordLimit={wordLimit}
              sectionWordCounts={sectionWordCounts}
              sectionLimits={validation?.section_limits}
              showSectionBreakdown={true}
              size="lg"
            />
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {enableOptimizer && (
                <Button
                  variant="default"
                  onClick={() => setShowOptimizer(true)}
                  disabled={!wordLimit || wordCount <= wordLimit}
                  title={
                    !wordLimit
                      ? 'Please set a word limit first'
                      : wordCount <= wordLimit
                      ? 'Word count is within limit'
                      : `Optimize to reduce from ${wordCount.toLocaleString()} to ${wordLimit.toLocaleString()} words`
                  }
                >
                  <Scissors className="h-4 w-4 mr-2" />
                  Optimize Word Count
                  {wordLimit && wordCount > wordLimit && (
                    <span className="ml-2 text-xs">
                      ({((wordCount - wordLimit) / wordLimit * 100).toFixed(0)}% over)
                    </span>
                  )}
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => setShowLimitConfig(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Configure Limits
              </Button>
            </div>
            
            {/* Validation Warnings */}
            {validation && !validation.is_compliant && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                      Word Count Limit Exceeded
                    </h4>
                    {validation.violations.total.length > 0 && (
                      <ul className="text-sm text-red-800 dark:text-red-200 list-disc list-inside space-y-1 mb-2">
                        {validation.violations.total.map((v, i) => (
                          <li key={i}>{v.message}</li>
                        ))}
                      </ul>
                    )}
                    {validation.violations.sections.length > 0 && (
                      <ul className="text-sm text-red-800 dark:text-red-200 list-disc list-inside space-y-1">
                        {validation.violations.sections.map((v, i) => (
                          <li key={i}>{v.message}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                      Please optimize the word count before submission.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      {/* Word Count Optimizer Modal */}
      {showOptimizer && report && (
        <Modal
          isOpen={true}
          onClose={() => setShowOptimizer(false)}
          title="Word Count Optimization"
          size="xl"
        >
          <div className="max-h-[80vh] overflow-y-auto">
            <WordCountOptimizer
              report={report}
              currentWordCount={wordCount}
              targetWordCount={wordLimit}
              onOptimizationComplete={() => {
                queryClient.invalidateQueries({ queryKey: ['report', validReportId] });
                queryClient.invalidateQueries({ queryKey: ['word-count-validation', validReportId] });
                setShowOptimizer(false);
              }}
              onClose={() => setShowOptimizer(false)}
            />
          </div>
        </Modal>
      )}
      
      {/* Word Limit Configuration Modal */}
      {showLimitConfig && report && (
        <Modal
          isOpen={true}
          onClose={() => setShowLimitConfig(false)}
          title="Word Limit Configuration"
          size="lg"
        >
          <WordLimitConfiguration
            reportId={validReportId}
            currentWordLimit={wordLimit}
            sectionLimits={sectionLimitsById}
            sections={report.sections || []}
            onSave={async (limits) => {
              // Optimistically update the report cache with new limits for immediate UI update
              queryClient.setQueryData(['report', validReportId], (oldData: any) => {
                if (!oldData) return oldData;
                const updatedMeta = {
                  ...(oldData.meta || {}),
                  ...(limits.total_limit !== undefined && { word_limit: limits.total_limit }),
                  ...(limits.section_limits !== undefined && { section_limits: limits.section_limits }),
                };
                return {
                  ...oldData,
                  meta: updatedMeta,
                };
              });
              
              // Refetch queries to ensure we have the latest data from the server
              await Promise.all([
                queryClient.refetchQueries({ queryKey: ['report', validReportId] }),
                queryClient.refetchQueries({ queryKey: ['word-count-validation', validReportId] }),
              ]);
              
              setShowLimitConfig(false);
            }}
          />
        </Modal>
      )}
    </>
  );
}
